import {
  Box,
  Typography,
  Grid,
  Container,
  Stack,
  ButtonBase,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import CardPackage from "../components/CardPackage";

import { usePackages } from "../hooks/usePackages";
import { useCartStore } from "../store/useCartStore";

import { setSecureItem } from "../utils/aes";
import { getLastVisitStep } from "../service/setLastPath";
import { inAppLogin } from "../api/auth";
import { useSnackbarStore } from "../store/useSnackbarStore";
import { mapPackageToCartItem } from "../utils/mappers/packageToCart";
import { PackageModel } from "../repositories/package.repository";
import { gradients } from "../styles/theme";
import {
  fadeUp,
  staggerContainer,
  staggerItem,
  viewportOnce,
} from "../styles/motion";

const MotionGrid = motion(Grid);

const Home = () => {
  const { data: packageCategory, isLoading, isError } = usePackages();

  const [activeTab, setActiveTab] = useState(0);

  const localClearCart = useCartStore((s) => s.clearCart);
  const cartItems = useCartStore((s) => s.cartItems);
  const getQuantityById = useCartStore((s) => s.getQuantityById);

  const location = useLocation();
  const navigate = useNavigate();

  const lastVisitPath = getLastVisitStep();

  const showSnackbar = useSnackbarStore((s) => s.showSnackbar);
  const addToCart = useCartStore((s) => s.addToCart);
  const totalQty = useCartStore((s) => s.getTotalQuantity());

  const getCategoryQuantity = (categoryPackages: PackageModel[]) =>
    categoryPackages.reduce(
      (sum, pkg) => sum + getQuantityById(pkg.packageId),
      0,
    );

  /* ------------------ Effects ------------------ */
  useEffect(() => {
    if (lastVisitPath === "payment") {
      localClearCart();
    }
  }, [lastVisitPath, localClearCart]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const username = params.get("username");
    const password = params.get("password");
    const apikey = params.get("apikey");
    const msisdn = params.get("msisdn");

    if (username && password && apikey) {
      setSecureItem("username", username);
      setSecureItem("password", password);
      setSecureItem("apikey", apikey);

      msisdn ? inAppLogin(msisdn) : navigate("/");
    }
  }, [location.search, navigate]);

  /* ------------------ Handlers ------------------ */
  const handleOnSelect = (pkg: PackageModel) => {
    navigate(`/package-detail`, { state: { pkg } });
  };

  const handleAddToCart = (pkg: PackageModel) => {
    if (totalQty >= 5) {
      showSnackbar("You can't add more than 5 items", "warning");
      return;
    }
    addToCart([mapPackageToCartItem(pkg)]);
    showSnackbar("Added to cart", "success");
  };

  /* ------------------ Loading / Error States ------------------ */
  if (isLoading)
    return (
      <Container
        sx={{
          py: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <CircularProgress color="primary" />
        <Typography variant="body2" color="text.secondary">
          Loading packages…
        </Typography>
      </Container>
    );

  if (isError || !packageCategory?.length)
    return (
      <Container sx={{ py: 10, textAlign: "center" }}>
        <Typography variant="h3" gutterBottom>
          No packages found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please check back shortly.
        </Typography>
      </Container>
    );

  const activeCategory = packageCategory[activeTab];

  /* ------------------ UI ------------------ */
  return (
    <Box sx={{ position: "relative", minHeight: "100vh" }}>
      {/* Banner band */}
      <Box
        component={motion.div}
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        sx={{ width: "100%", position: "relative", overflow: "hidden" }}
      >
        <picture>
          <source
            srcSet="ltc_tourist_esim_banner_mobile.jpeg"
            media="(max-width:600px)"
          />
          <img
            src="ltc_tourist_esim_banner.jpg"
            alt="Lao Telecom Tourist eSIM"
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </picture>
      </Box>

      {/* Catalog section */}
      <Box
        component="section"
        sx={{
          background: gradients.subtle,
          pb: cartItems.length > 0 ? { xs: 14, md: 16 } : { xs: 4, md: 8 },
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              position: "sticky",
              top: { xs: 56, sm: 64 },
              zIndex: 2,
              mx: { xs: -2, sm: -3 },
              mb: { xs: 3, md: 5 },
              bgcolor: "background.default",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{
                overflowX: "auto",
                px: { xs: 2, sm: 3 },
                py: 1.5,
                scrollSnapType: "x mandatory",
                scrollPaddingLeft: { xs: 16, sm: 24 },
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {packageCategory.map((cat, i) => {
                const active = i === activeTab;
                const qty = getCategoryQuantity(cat.packages);
                return (
                  <ButtonBase
                    key={cat.categoryName}
                    onClick={() => setActiveTab(i)}
                    focusRipple
                    sx={{
                      flexShrink: 0,
                      scrollSnapAlign: "start",
                      borderRadius: 999,
                      px: 2.25,
                      height: 38,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      letterSpacing: "0.01em",
                      transition:
                        "background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
                      background: active
                        ? gradients.hero
                        : "rgba(15,15,15,0.04)",
                      color: active ? "#FFFFFF" : "text.primary",
                      boxShadow: active
                        ? "0 6px 16px rgba(232,0,13,0.25)"
                        : "none",
                      "&:hover": {
                        background: active
                          ? gradients.hero
                          : "rgba(15,15,15,0.08)",
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      component="span"
                    >
                      <span>{cat.categoryName}</span>
                      {qty > 0 && (
                        <Box
                          component="span"
                          sx={{
                            minWidth: 20,
                            height: 20,
                            px: 0.75,
                            borderRadius: 999,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 700,
                            bgcolor: active
                              ? "rgba(255,255,255,0.25)"
                              : "primary.main",
                            color: "#FFFFFF",
                          }}
                        >
                          {qty}
                        </Box>
                      )}
                    </Stack>
                  </ButtonBase>
                );
              })}
            </Stack>
          </Box>

          {/* Package grid */}
          <Grid
            component={motion.div}
            container
            rowSpacing={2}
            columnSpacing={{ xs: 3, md: 2 }}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            key={activeCategory.categoryName}
          >
            {activeCategory.packages.map((pkg) => (
              <MotionGrid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={pkg.packageId}
                variants={staggerItem}
                sx={{ display: "flex" }}
              >
                <CardPackage
                  package={pkg}
                  numSelected={getQuantityById(pkg.packageId)}
                  onSelect={() => handleOnSelect(pkg)}
                  onAddToCart={() => handleAddToCart(pkg)}
                />
              </MotionGrid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
